import { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered } from "lucide-react";

// Lightweight rich-text editor (contentEditable) producing HTML.
// Supports bold, italic, bullet and numbered lists.
export default function RichTextEditor({ value, onChange, placeholder = "", testId }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (cmd) => {
    ref.current?.focus();
    document.execCommand(cmd, false, null);
    emit();
  };

  const Btn = ({ cmd, icon: Icon, label }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        exec(cmd);
      }}
      title={label}
      data-testid={testId ? `${testId}-${cmd}` : undefined}
      className="h-7 w-7 flex items-center justify-center rounded-md text-zinc-300 hover:text-white hover:bg-white/10 transition-colors"
    >
      <Icon size={14} />
    </button>
  );

  return (
    <div className="rounded-md border border-white/10 bg-white/5 overflow-hidden focus-within:border-fuchsia-500">
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-white/10 bg-black/20">
        <Btn cmd="bold" icon={Bold} label="Grassetto" />
        <Btn cmd="italic" icon={Italic} label="Corsivo" />
        <span className="w-px h-4 bg-white/10 mx-1" />
        <Btn cmd="insertUnorderedList" icon={List} label="Elenco puntato" />
        <Btn cmd="insertOrderedList" icon={ListOrdered} label="Elenco numerato" />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder}
        data-testid={testId}
        className="rte-content min-h-[120px] max-h-64 overflow-y-auto px-3 py-2 text-sm text-zinc-100 outline-none leading-relaxed"
      />
    </div>
  );
}
